/**
 * SocialLinks - Social platform links display
 *
 * Renders a row of clickable social icons for connected platforms.
 * Each icon links to the platform profile URL.
 */

import { SocialProfileResponse, SocialPlatform } from "@/hooks/hub/useGetHub";
import {
    FaTwitch,
    FaYoutube,
    FaTwitter,
    FaTiktok,
    FaInstagram,
    FaDiscord,
    FaFacebook,
    FaGlobe,
} from "react-icons/fa";
import { SiKick, SiStreamlabs } from "react-icons/si";
import "./SocialLinks.scss";

interface SocialLinksProps {
    socials: SocialProfileResponse[];
    size?: "small" | "medium" | "large";
    showFollowers?: boolean;
}

// Platform icon mapping
const PLATFORM_ICONS: Record<SocialPlatform, React.ComponentType<{ className?: string }>> = {
    TWITCH: FaTwitch,
    YOUTUBE: FaYoutube,
    KICK: SiKick,
    TWITTER: FaTwitter,
    TIKTOK: FaTiktok,
    INSTAGRAM: FaInstagram,
    DISCORD: FaDiscord,
    FACEBOOK: FaFacebook,
    STREAMLABS: SiStreamlabs,
    TWITCH_TEAM: FaTwitch,
    OTHER: FaGlobe,
};

// Platform colors for hover effects
const PLATFORM_COLORS: Record<SocialPlatform, string> = {
    TWITCH: "#9146FF",
    YOUTUBE: "#FF0000",
    KICK: "#53FC18",
    TWITTER: "#1DA1F2",
    TIKTOK: "#000000",
    INSTAGRAM: "#E4405F",
    DISCORD: "#5865F2",
    FACEBOOK: "#1877F2",
    STREAMLABS: "#80F5D2",
    TWITCH_TEAM: "#9146FF",
    OTHER: "#888888",
};

// Platform display names
const PLATFORM_NAMES: Record<SocialPlatform, string> = {
    TWITCH: "Twitch",
    YOUTUBE: "YouTube",
    KICK: "Kick",
    TWITTER: "Twitter",
    TIKTOK: "TikTok",
    INSTAGRAM: "Instagram",
    DISCORD: "Discord",
    FACEBOOK: "Facebook",
    STREAMLABS: "Streamlabs",
    TWITCH_TEAM: "Twitch Team",
    OTHER: "Website",
};

// Format follower count for display (e.g., 1.2M, 500K)
function formatFollowers(count?: number): string {
    if (!count) return "";
    if (count >= 1000000) {
        return `${(count / 1000000).toFixed(1).replace(/\.0$/, "")}M`;
    }
    if (count >= 1000) {
        return `${(count / 1000).toFixed(1).replace(/\.0$/, "")}K`;
    }
    return count.toString();
}

// Validate URL is safe (http/https only, no javascript: or data:)
function isValidUrl(url?: string): boolean {
    if (!url) return false;
    try {
        const parsed = new URL(url);
        return parsed.protocol === "http:" || parsed.protocol === "https:";
    } catch {
        return false;
    }
}

/*
 * Platform base URLs for constructing profile links
 * Only include platforms with predictable URL patterns
 */
const PLATFORM_BASE_URLS: Partial<Record<SocialPlatform, string>> = {
    TWITCH: "https://twitch.tv/",
    YOUTUBE: "https://youtube.com/@",
    KICK: "https://kick.com/",
    TWITTER: "https://twitter.com/",
    TIKTOK: "https://tiktok.com/@",
    INSTAGRAM: "https://instagram.com/",
    DISCORD: "https://discord.gg/",
    FACEBOOK: "https://facebook.com/",
    // STREAMLABS excluded - URLs have unpredictable paths like /username/merch
    TWITCH_TEAM: "https://twitch.tv/team/",
};

// Generate profile URL from platform and username if not provided
function getProfileUrl(social: SocialProfileResponse): string | undefined {
    // Use explicit profileUrl if valid
    if (isValidUrl(social.profileUrl)) {
        return social.profileUrl;
    }
    // Otherwise construct from platform + username
    const baseUrl = PLATFORM_BASE_URLS[social.platform];
    if (baseUrl && social.username) {
        return `${baseUrl}${social.username}`;
    }
    return undefined;
}

export const SocialLinks = ({
    socials,
    size = "medium",
    showFollowers = false,
}: SocialLinksProps) => {
    if (!socials || socials.length === 0) {
        return null;
    }

    // Map socials to include generated profile URLs, then filter those without valid URLs
    const socialsWithUrls = socials.map((s) => ({
        ...s,
        resolvedProfileUrl: getProfileUrl(s),
    })).filter((s) => s.resolvedProfileUrl);

    if (socialsWithUrls.length === 0) {
        return null;
    }

    return (
        <div className={`social-links social-links--${size}`}>
            {socialsWithUrls.map((social) => {
                const Icon = PLATFORM_ICONS[social.platform] || FaDiscord;
                const color = PLATFORM_COLORS[social.platform] || "#ffffff";
                const platformName = PLATFORM_NAMES[social.platform] || social.platform;
                const followers = formatFollowers(social.followers);

                return (
                    <a
                        key={`${social.platform}-${social.platformUserId || social.username}`}
                        href={social.resolvedProfileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="social-links__link"
                        title={`${social.displayName || social.username} on ${platformName}${followers ? ` (${followers} followers)` : ""}`}
                        style={{ "--platform-color": color } as React.CSSProperties}
                    >
                        <Icon className="social-links__icon" />
                        {showFollowers && followers &&
                            <span className="social-links__followers">
                                {followers}
                            </span>
                        }
                    </a>
                );
            })}
        </div>
    );
};
